import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { adminAPI, writeupAPI } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Save, Eye, EyeOff, ArrowLeft, Upload, Image as ImageIcon,
  X, Shield, Linkedin, CheckCircle, Terminal, ChevronRight,
  List, Hash, FileText, Cpu, Tag, Search,
} from "lucide-react";
import DOMPurify from "dompurify";

/* ─── font + style injection ─── */
function useStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-ctf-fonts]")) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Syne:wght@700;800&family=JetBrains+Mono:wght@300;400;500;700&display=swap";
      l.setAttribute("data-ctf-fonts", "1");
      document.head.appendChild(l);
    }
    if (!document.querySelector("style[data-adm2]")) {
      const s = document.createElement("style");
      s.setAttribute("data-adm2", "1");
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ─── SKILLS DATA ─── */
const SKILLS_DATA = [
  {
    label: "Web Exploitation", cls: "sk-web",
    tags: [
      "SQLi","Blind SQLi","Error-Based SQLi","Union SQLi","Time-Based SQLi",
      "XSS","Stored XSS","Reflected XSS","DOM XSS","SSTI","SSRF","Blind SSRF",
      "LFI","RFI","RCE","File Upload","Path Traversal","Open Redirect","IDOR","BOLA",
      "JWT Forgery","JWT None Algorithm","OAuth Abuse","CORS Bypass","Cookie Manipulation",
      "Session Fixation","CSRF","Clickjacking","HTTP Request Smuggling","WebSocket Hijack",
      "API Abuse","GraphQL Injection","Mass Assignment","Business Logic","Race Condition",
      "Type Juggling","PHP Object Injection","Prototype Pollution","Cache Poisoning",
      "Host Header Injection","HTTP Parameter Pollution","Subdomain Takeover",
      "2FA Bypass","WAF Bypass","Rate Limit Bypass","Broken Auth",
    ],
  },
  {
    label: "Injection", cls: "sk-inject",
    tags: [
      "Command Injection","Blind CMDi","XXE","Blind XXE","XPath Injection",
      "LDAP Injection","NoSQLi","Log Poisoning","Log4Shell","Template Injection",
      "Deserialization","Java Deserialization",".NET Deserialization",
      "YAML Injection","EL Injection","Header Injection","HTML Injection","CSS Injection",
    ],
  },
  {
    label: "Linux PrivEsc", cls: "sk-privl",
    tags: [
      "SUID Abuse","SGID Abuse","Sudo Abuse","Cron Abuse","Capabilities",
      "Wildcard Injection","PATH Hijacking","LD_PRELOAD","Writable /etc/passwd",
      "NFS No_root_squash","Docker Breakout","LXD/LXC Abuse","Kernel Exploit",
      "Dirty COW","Dirty Pipe","PwnKit","MySQL UDF","Python Lib Hijack",
      "Shared Object Injection","Systemd Abuse","D-Bus Abuse",
      "Weak File Permissions","SUID Shell",
    ],
  },
  {
    label: "Windows PrivEsc", cls: "sk-privw",
    tags: [
      "Token Impersonation","SeImpersonatePrivilege","SeDebugPrivilege",
      "SeBackupPrivilege","SeRestorePrivilege","AlwaysInstallElevated",
      "Writable Service","Unquoted Service Path","DLL Hijacking","DLL Side-Loading",
      "ACL Abuse","Registry Abuse","Scheduled Task Abuse","UAC Bypass",
      "PrintSpoofer","JuicyPotato","RoguePotato","GodPotato","SweetPotato",
      "AMSI Bypass","SAM Dump","LSASS Dump","Credential Manager","Startup Abuse",
      "Process Injection",
    ],
  },
  {
    label: "Active Directory", cls: "sk-ad",
    tags: [
      "Kerberoasting","AS-REP Roasting","Pass-the-Hash","Pass-the-Ticket",
      "Overpass-the-Hash","DCSync","Golden Ticket","Silver Ticket","Skeleton Key",
      "BloodHound","ACL Abuse","GPO Abuse","DACL Abuse","ForceChangePassword",
      "GenericWrite","WriteDACL","Constrained Delegation","Unconstrained Delegation",
      "Resource-Based Delegation","PrintNightmare","ZeroLogon","PetitPotam","NoPac",
      "NTLM Relay","SMB Relay","LLMNR Poisoning","NBT-NS Poisoning","mitm6",
      "Cross-Domain Trust","Child-to-Parent",
    ],
  },
  {
    label: "Network & Services", cls: "sk-net",
    tags: [
      "FTP Anonymous","SMB Null Session","RPC Enum","SNMP Enum","NFS Exploit",
      "SMTP User Enum","DNS Zone Transfer","ARP Spoofing","DNS Poisoning",
      "Port Knocking","Redis Exploit","MongoDB NoAuth","Elasticsearch",
      "Jenkins RCE","Tomcat RCE","WebLogic RCE","JBoss RCE","Drupal RCE",
      "WordPress Plugin RCE","GitLab RCE","VNC Exploit","RDP Exploit","Telnet Abuse",
    ],
  },
  {
    label: "Cryptography", cls: "sk-crypto",
    tags: [
      "Hash Cracking","Rainbow Table","Padding Oracle","CBC Bit Flipping",
      "ECB Oracle","RSA Low Exponent","RSA Common Modulus","RSA Factoring",
      "Weak PRNG","XOR Cipher","Vigenere","Caesar / ROT","Base32/64/85",
      "JWT RS256→HS256","Length Extension","ECDSA Nonce Reuse","Weak IV",
    ],
  },
  {
    label: "Forensics & Stego", cls: "sk-stego",
    tags: [
      "LSB Steganography","Audio Stego","EXIF Metadata","Binwalk","File Carving",
      "Disk Forensics","Memory Forensics","Volatility","PCAP Analysis",
      "Log Analysis","Hex Analysis","Strings Analysis","Zip Password Crack","PDF Metadata",
    ],
  },
  {
    label: "Binary Exploitation", cls: "sk-bin",
    tags: [
      "Buffer Overflow","Stack Overflow","Heap Overflow","Use-After-Free",
      "Format String","ROP Chain","ret2libc","ret2plt","ret2win",
      "ASLR Bypass","PIE Bypass","NX Bypass","Canary Bypass","GOT Overwrite",
      "Shellcode","Tcache Poisoning","Fast Bin Attack","Integer Overflow",
      "Off-by-One","Type Confusion",
    ],
  },
  {
    label: "Reverse Engineering", cls: "sk-re",
    tags: [
      "Static Analysis","Dynamic Analysis","Ghidra","IDA Pro","x64dbg",
      "GDB / pwndbg","Radare2","Anti-Debug Bypass","Anti-VM Bypass",
      "Unpacking","Deobfuscation","Firmware Analysis",".NET Reversing",
      "Java Reversing","APK Reversing","Patching Binary","Keygen",
    ],
  },
  {
    label: "Cloud & Container", cls: "sk-cloud",
    tags: [
      "AWS SSRF → IMDSv1","S3 Misconfiguration","IAM PrivEsc","Lambda Abuse",
      "Azure AD Enum","GCP Metadata","Docker Escape","K8s RBAC Abuse",
      "K8s API Server","Exposed Secrets in ENV","CI/CD Abuse","GitHub Actions Abuse",
    ],
  },
  {
    label: "Miscellaneous", cls: "sk-misc",
    tags: [
      "Default Credentials","Credential Reuse","Hardcoded Secrets","Git Exposure",
      "Backup File Disclosure","Source Code Disclosure","Null Byte Injection",
      "Unicode Bypass","Weak Regex","Password in Logs","Env Variable Leak",
      "Certificate Transparency","WHOIS Recon",
    ],
  },
];

const TECHNIQUES_DATA = [
  {
    label: "Reconnaissance", cls: "tk-recon",
    tags: [
      "Port Scanning","Service Fingerprinting","OS Detection","Directory Bruting",
      "File Extension Bruting","Virtual Host Discovery","Subdomain Enumeration",
      "DNS Enumeration","DNS Zone Transfer","SMB Enumeration","SNMP Walk",
      "LDAP Enumeration","RPC Enumeration","User Enumeration","OSINT",
      "Google Dorking","GitHub Dorking","Shodan","Wayback Machine","Fuzzing",
      "Parameter Discovery","API Endpoint Discovery","JS File Analysis",
      "Source Map Extraction","Robots.txt","Certificate Transparency","Email Harvesting",
    ],
  },
  {
    label: "Exploitation", cls: "tk-exp",
    tags: [
      "Exploit Chaining","CVE Exploitation","Public PoC Abuse","Metasploit",
      "Manual Exploitation","Brute Force","Password Spraying","Credential Stuffing",
      "Wordlist Attack","Mask Attack","Rule-Based Attack","Phishing",
      "Client-Side Attack","Malicious Macro","WAF Bypass","Filter Evasion",
      "Double Encoding","Null Byte Bypass","Case Manipulation",
    ],
  },
  {
    label: "Post-Exploitation", cls: "tk-post",
    tags: [
      "Reverse Shell","Bind Shell","Web Shell","Meterpreter","Persistence",
      "Credential Dumping","SAM / NTDS Dump","LSASS Dump","Mimikatz","Keylogging",
      "Data Exfiltration","Exfil via DNS","Exfil via ICMP","Log Clearing",
      "DLL Injection","Reflective DLL","Scheduled Task Backdoor","Service Backdoor",
      "Registry Backdoor","SSH Key Persistence","Crontab Backdoor",
      "WMI Subscription","Browser Password Dump",
    ],
  },
  {
    label: "Lateral Movement", cls: "tk-lat",
    tags: [
      "Pass-the-Hash","Pass-the-Ticket","Overpass-the-Hash","Golden Ticket",
      "Silver Ticket","WMI Execution","PSExec","SMBExec","WinRM Abuse",
      "RDP Hijacking","SSH Agent Forwarding","DCOM Execution","BloodHound Path",
      "Kerberos Delegation","Token Impersonation","Credential Reuse",
      "AT Job Execution","SCM Abuse",
    ],
  },
  {
    label: "Evasion & Bypass", cls: "tk-evas",
    tags: [
      "AMSI Bypass","UAC Bypass","ETW Patching","AV Evasion","EDR Bypass",
      "Obfuscation","Payload Encoding","LOLBAS","Living off the Land",
      "Signed Binary Proxy","Parent PID Spoofing","Timestomping",
      "Alternate Data Streams","AppLocker Bypass","CLM Bypass",
      "Firewall Evasion","IDS/IPS Evasion",
    ],
  },
  {
    label: "Tunneling & Pivoting", cls: "tk-tunnel",
    tags: [
      "SSH Tunneling","SSH Dynamic Forward","SSH Reverse Tunnel","Port Forwarding",
      "Chisel","Ligolo-ng","Proxychains","SOCKS5 Proxy","HTTP Tunnel",
      "DNS Tunneling","ICMP Tunneling","Double Pivot","Metasploit Route",
      "Rpivot","VPN Pivoting",
    ],
  },
];

/* flat lists used for the preview panel chips */
const ALL_SKILLS     = SKILLS_DATA.flatMap(c => c.tags);
const ALL_TECHNIQUES = TECHNIQUES_DATA.flatMap(c => c.tags);

/* ─── constants ─── */
const DEFAULT_TEMPLATE = `# Overview

Write your writeup using Markdown. Paste or drag images directly!

## Reconnaissance
\`\`\`terminal
nmap -sC -sV -p- target.htb
\`\`\`

## Initial Access
...

## Privilege Escalation
...

## Lessons Learned
...`;

const DIFF = {
  easy:   { c: "#00ff88", r: "0,255,136"  },
  medium: { c: "#ff8c42", r: "255,140,66" },
  hard:   { c: "#ff4455", r: "255,68,85"  },
  insane: { c: "#cc44ff", r: "204,68,255" },
};

const PLAT = { htb: "Hack The Box", proving_grounds: "Proving Grounds", other: "Other" };
const splitCsv = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);

/* ─── tiny components ─── */
const Blink = () => <span className="a-blink" />;

const Step = ({ n, icon: Icon, label }) => (
  <div className="a-step">
    <span className="a-step-n">{String(n).padStart(2,"0")}</span>
    {Icon && <Icon size={13} className="a-step-icon" />}
    <span className="a-step-label">{label}</span>
    <span className="a-step-rule" />
  </div>
);

const FL = ({ children, req }) => (
  <div className="a-fl">
    <span className="a-fl-arr">›</span>
    {children}
    {req && <span className="a-fl-req">*</span>}
  </div>
);

/* ─── TagSelector component ─── */
const TagSelector = ({ data, selected, onToggle, accentClass }) => {
  const [query,      setQuery]      = useState("");
  const [activeTab,  setActiveTab]  = useState(data[0]?.label || "");

  const q = query.toLowerCase().trim();

  /* when searching show ALL categories flat, otherwise show active tab */
  const display = q
    ? data.map(cat => ({ ...cat, tags: cat.tags.filter(t => t.toLowerCase().includes(q)) }))
        .filter(cat => cat.tags.length > 0)
    : data.filter(cat => cat.label === activeTab);

  const totalSelected = selected.length;

  return (
    <div className="ts-wrap">
      {/* search */}
      <div className="ts-search-wrap">
        <Search size={12} className="ts-search-icon" />
        <input
          className="ts-search"
          placeholder="filter tags…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {totalSelected > 0 && (
          <span className="ts-sel-count">{totalSelected} selected</span>
        )}
      </div>

      {/* tabs — hidden while searching */}
      {!q && (
        <div className="ts-tabs">
          {data.map(cat => (
            <button
              key={cat.label}
              type="button"
              className={`ts-tab${activeTab === cat.label ? " active" : ""}${accentClass === "cyan" ? " ts-tab-cyan" : ""}`}
              onClick={() => setActiveTab(cat.label)}
            >
              {cat.label}
              {selected.filter(s => cat.tags.includes(s)).length > 0 && (
                <span className="ts-tab-badge">
                  {selected.filter(s => cat.tags.includes(s)).length}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* tags */}
      <div className="ts-body">
        {display.map(cat => (
          <div key={cat.label}>
            {q && <div className="ts-cat-label">{cat.label}</div>}
            <div className="ts-tags">
              {cat.tags.map(tag => {
                const on = selected.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`a-chip${accentClass === "cyan" ? " a-chip-c" : ""}${on ? " is-on" : ""}`}
                    onClick={() => onToggle(tag)}
                  >
                    {on && <span className="a-dot" />}
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {display.length === 0 && (
          <div className="ts-empty">no tags match "{query}"</div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
const AdminWriteupEditorTest = () => {
  useStyles();

  const { id }     = useParams();
  const navigate   = useNavigate();
  const { isAuthenticated, checkSession, getAuthHeader } = useAdmin();

  const taRef   = useRef(null);
  const fileRef = useRef(null);

  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(false);
  const [dragging,  setDragging]  = useState(false);
  const [pubOk,     setPubOk]     = useState(false);
  const [pubId,     setPubId]     = useState(null);

  const [form, setForm] = useState({
    title: "", description: "", content: DEFAULT_TEMPLATE,
    difficulty: "easy", platform: "htb", machine_name: "",
    cover_image: "", os_type: "linux",
    tags: "", skills: [], techniques: [], cves: "", tools_used: "",
    published: true,
  });

  const load = useCallback(async () => {
    try {
      const { data: w } = await writeupAPI.getOne(id);
      setForm({
        title: w.title || "", description: w.description || "",
        content: w.content || DEFAULT_TEMPLATE,
        difficulty: w.difficulty || "easy", platform: w.platform || "htb",
        machine_name: w.machine_name || "", cover_image: w.cover_image || "",
        os_type: w.os_type || "linux",
        tags:       (w.tags       || []).join(", "),
        skills:      w.skills     || [],
        techniques:  w.techniques || [],
        cves:       (w.cves       || []).join(", "),
        tools_used: (w.tools_used || []).join(", "),
        published: w.published !== false,
      });
    } catch { toast.error("Failed to load writeup"); navigate("/admin"); }
  }, [id, navigate]);

  /* auth + load */
  useEffect(() => {
    (async () => {
      const ok = await checkSession();
      if (!ok) { navigate("/admin"); return; }
      if (id) await load();
      setLoading(false);
    })();
  }, [id, checkSession, load, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const chg = e => set(e.target.name, e.target.value);
  const tog = (k, v) => setForm(f => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v],
  }));

  /* insertBlock */
  const insertBlock = (type) => {
    if (!taRef.current) return;
    const ta = taRef.current;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const blocks = {
      terminal: "\n```terminal\n\n```\n",
      output:   "\n```output\n\n```\n",
      bullet:   "\n- Item 1\n- Item 2\n- Item 3\n",
      number:   "\n1. Step one\n2. Step two\n3. Step three\n",
    };
    const block = blocks[type] || "";
    const text  = form.content;
    setForm(f => ({ ...f, content: text.slice(0,start) + block + text.slice(end) }));
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + block.length; ta.focus(); }, 0);
  };

  /* uploadImage */
  const uploadImage = useCallback(async (file) => {
    if (!file) return;
    if (!["image/png","image/jpeg","image/webp"].includes(file.type)) { toast.error("PNG/JPG/WebP only"); return; }
    if (file.size > 5*1024*1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const { data: { markdown } } = await adminAPI.uploadImage(file, getAuthHeader());
      if (taRef.current) {
        const ta = taRef.current, s = ta.selectionStart, e2 = ta.selectionEnd;
        setForm(f => ({ ...f, content: f.content.slice(0,s) + "\n" + markdown + "\n" + f.content.slice(e2) }));
        setTimeout(() => { ta.selectionStart = ta.selectionEnd = s + markdown.length + 2; ta.focus(); }, 0);
      }
      toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  }, [getAuthHeader]);

  /* drag/drop */
  const onDragEnter = e => { e.preventDefault(); e.stopPropagation(); setDragging(true);  };
  const onDragLeave = e => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const onDragOver  = e => { e.preventDefault(); e.stopPropagation(); };
  const onDrop      = async e => {
    e.preventDefault(); e.stopPropagation(); setDragging(false);
    if (e.dataTransfer.files[0]) await uploadImage(e.dataTransfer.files[0]);
  };

  const onPaste = async e => {
    for (const item of e.clipboardData?.items || []) {
      if (item.type.startsWith("image/")) { e.preventDefault(); await uploadImage(item.getAsFile()); return; }
    }
  };

  /* global paste listener */
  useEffect(() => {
    if (preview) return;
    const handler = async (e) => {
      if (e.target === taRef.current) return;
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          taRef.current?.focus();
          await uploadImage(item.getAsFile());
          return;
        }
      }
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [preview, uploadImage]);

  const validateForm = () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return false;
    }
    if (!form.description.trim()) {
      toast.error("Description is required");
      return false;
    }
    if (!form.content.trim()) {
      toast.error("Writeup content is required");
      return false;
    }
    if (splitCsv(form.tags).length === 0) {
      toast.error("Add at least one tag");
      return false;
    }
    return true;
  };

  /* handleSubmit */
  const handleSubmit = async (e, publishOverride = form.published) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        published: publishOverride,
        machine_name: form.machine_name || null,
        cover_image:  form.cover_image  || null,
        os_type:      form.os_type      || null,
        tags:       splitCsv(form.tags),
        cves:       splitCsv(form.cves),
        tools_used: splitCsv(form.tools_used),
      };
      if (id) {
        await adminAPI.updateWriteup(id, payload, getAuthHeader());
        set("published", publishOverride);
        toast.success(publishOverride ? "Writeup updated" : "Draft updated");
        if (publishOverride) { setPubId(id); setPubOk(true); }
      } else {
        const { data } = await adminAPI.createWriteup(payload, getAuthHeader());
        set("published", publishOverride);
        toast.success(publishOverride ? "Writeup published" : "Draft created");
        if (publishOverride) { setPubId(data.id); setPubOk(true); }
        else navigate(`/admin/writeup/${data.id}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save writeup"); }
    finally { setSaving(false); }
  };

  /* markdown preview */
  const renderMarkdownPreview = (content) => {
    let html = content;
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/gim, (_, lang, code) => {
      if (lang==="terminal") return `<pre class="apv-terminal"><code>${code}</code></pre>`;
      if (lang==="output")   return `<pre class="apv-output"><code>${code}</code></pre>`;
      return `<pre class="apv-code"><code>${code}</code></pre>`;
    });
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim,'<img src="$2" alt="$1" class="apv-img" loading="lazy" />');
    html = html.replace(/^### (.*$)/gim,'<h3 class="apv-h3">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="apv-h2">$1</h2>');
    html = html.replace(/^# (.*$)/gim,  '<h1 class="apv-h1">$1</h1>');
    html = html.replace(/\*\*(.*?)\*\*/gim,'<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/gim,   '<em>$1</em>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim,'<a href="$2" target="_blank" rel="noopener noreferrer" class="apv-a">$1</a>');
    html = html.replace(/`([^`]+)`/gim,'<code class="apv-ic">$1</code>');
    html = html.replace(/^> (.*$)/gim,'<blockquote class="apv-bq">$1</blockquote>');
    html = html.replace(/\n/gim,'<br/>');
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS:['h1','h2','h3','p','br','strong','em','a','code','pre','blockquote','img','ul','ol','li'],
      ALLOWED_ATTR:['href','target','rel','src','alt','class'],
    });
  };

  const getWriteupUrl  = () => `${window.location.origin}/writeup/${pubId}`;
  const getLinkedInUrl = () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getWriteupUrl())}`;

  const diff    = DIFF[form.difficulty] || DIFF.easy;
  const machine = (form.machine_name || form.title || "MACHINE").toUpperCase();
  const words   = (form.content||"").trim().split(/\s+/).filter(Boolean).length;
  const mins    = Math.max(1, Math.round(words/200));
  const tagCount = splitCsv(form.tags).length;
  const toolCount = splitCsv(form.tools_used).length;

  if (loading) return (
    <div className="a-loading"><span>Initializing editor<Blink /></span></div>
  );
  if (!isAuthenticated) { navigate("/admin"); return null; }

  return (
    <div className="a-root" data-testid="admin-editor">

      {/* sticky topbar */}
      <div className="a-topbar">
        <div className="a-topbar-l">
          <button className="a-back" onClick={() => navigate("/admin")}>
            <ArrowLeft size={12} /> admin
          </button>
          <span className="a-sep">/</span>
          <span className="a-tp-path">
            writeup<span className="a-sep">/</span>
            <span style={{color:"#00ff88"}}>{id ? "edit" : "new"}</span>
          </span>
          <Blink />
        </div>
        <div className="a-topbar-r">
          <button className="a-btn a-ghost" type="button" onClick={() => setPreview(p=>!p)}>
            {preview ? <EyeOff size={13}/> : <Eye size={13}/>}
            {preview ? "Editor" : "Preview"}
          </button>
          <button className="a-btn a-outline" type="button"
            onClick={(e) => handleSubmit(e, false)} disabled={saving}>
            <Save size={13}/> Draft
          </button>
          <button className="a-btn a-primary" type="button" onClick={(e) => handleSubmit(e, true)} disabled={saving}>
            {saving ? "Saving…" : <><ChevronRight size={13}/>{id?"Update":"Publish"}</>}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="a-layout">

        {/* ════ LEFT ════ */}
        <div className="a-left">

          {/* 01 — basic info */}
          <div className="a-card">
            <Step n={1} icon={FileText} label="Basic Info" />
            <div className="a-g2 a-mb">
              <div className="a-field">
                <FL req>Title</FL>
                <input className="a-in" name="title" value={form.title} onChange={chg}
                  placeholder="e.g., HTB — Lame Walkthrough" data-testid="editor-title" />
              </div>
              <div className="a-field">
                <FL>Machine Name</FL>
                <input className="a-in" name="machine_name" value={form.machine_name} onChange={chg}
                  placeholder="e.g., Lame" data-testid="editor-machine" />
              </div>
            </div>
            <div className="a-field">
              <FL req>Description</FL>
              <textarea className="a-in a-ta" name="description" value={form.description}
                onChange={chg} rows={3} placeholder="Brief synopsis of the challenge…"
                data-testid="editor-description" />
            </div>
          </div>

          {/* 02 — classification */}
          <div className="a-card">
            <Step n={2} icon={Cpu} label="Classification" />
            <div className="a-g4">
              <div className="a-field">
                <FL req>Difficulty</FL>
                <div className="a-seg">
                  {Object.entries(DIFF).map(([d, cfg]) => (
                    <button key={d} type="button"
                      className={`a-seg-btn${form.difficulty===d?" is-on":""}`}
                      style={form.difficulty===d
                        ? {borderColor:cfg.c, color:cfg.c, background:`rgba(${cfg.r},.1)`}
                        : {}}
                      onClick={() => set("difficulty", d)}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="a-field">
                <FL req>Platform</FL>
                <div className="a-sel-wrap">
                  <select className="a-sel" value={form.platform}
                    onChange={e => set("platform", e.target.value)} data-testid="editor-platform">
                    <option value="htb">Hack The Box</option>
                    <option value="proving_grounds">Proving Grounds</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="a-field">
                <FL>OS Type</FL>
                <div className="a-seg">
                  {[{v:"linux",e:"🐧"},{v:"windows",e:"🪟"},{v:"other",e:"⚙"}].map(({v,e}) => (
                    <button key={v} type="button"
                      className={`a-seg-btn a-seg-cyan${form.os_type===v?" is-on":""}`}
                      onClick={() => set("os_type", v)} data-testid={`editor-os-${v}`}>
                      {e} {v}
                    </button>
                  ))}
                </div>
              </div>
              <div className="a-field">
                <FL>Visibility</FL>
                <div className="a-toggle-row">
                  <Switch checked={form.published} onCheckedChange={v => set("published", v)}
                    data-testid="editor-published" />
                  <span style={{color: form.published ? "#00ff88" : "#56657a", fontSize:12}}>
                    {form.published ? "Public" : "Draft"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 03 — skills */}
          <div className="a-card">
            <Step n={3} icon={Shield} label="Skills Used" />
            <TagSelector
              data={SKILLS_DATA}
              selected={form.skills}
              onToggle={v => tog("skills", v)}
              accentClass="green"
            />
          </div>

          {/* 04 — techniques */}
          <div className="a-card">
            <Step n={4} icon={Cpu} label="Techniques" />
            <TagSelector
              data={TECHNIQUES_DATA}
              selected={form.techniques}
              onToggle={v => tog("techniques", v)}
              accentClass="cyan"
            />
          </div>

          {/* 05 — metadata */}
          <div className="a-card">
            <Step n={5} icon={Tag} label="Metadata" />
            <div className="a-g3 a-mb">
              <div className="a-field">
                <FL>Tags</FL>
                <input className="a-in" name="tags" value={form.tags} onChange={chg}
                  placeholder="web, linux, retired" data-testid="editor-tags" />
              </div>
              <div className="a-field">
                <FL>CVEs</FL>
                <input className="a-in" name="cves" value={form.cves} onChange={chg}
                  placeholder="CVE-2021-1234" data-testid="editor-cves" />
              </div>
              <div className="a-field">
                <FL>Tools</FL>
                <input className="a-in" name="tools_used" value={form.tools_used} onChange={chg}
                  placeholder="nmap, burp, linpeas" data-testid="editor-tools" />
              </div>
            </div>
            <div className="a-field">
              <FL>Cover Image URL</FL>
              <div className="a-cover-row">
                <input className="a-in" name="cover_image" value={form.cover_image} onChange={chg}
                  placeholder="https://…" data-testid="editor-cover" />
                {form.cover_image && (
                  <img src={form.cover_image} alt="cover preview" className="a-cover-thumb" />
                )}
              </div>
            </div>
          </div>

          {/* 06 — content */}
          <div className="a-card">
            <Step n={6} icon={Terminal} label="Content (Markdown)" />
            <div className="a-toolbar">
              <div className="a-tb-l">
                {[
                  {t:"terminal", icon:<Terminal size={11}/>,     lbl:"Terminal"},
                  {t:"output",   icon:<ChevronRight size={11}/>, lbl:"Output"},
                  {t:"bullet",   icon:<List size={11}/>,         lbl:"• List"},
                  {t:"number",   icon:<Hash size={11}/>,         lbl:"1. List"},
                ].map(b => (
                  <button key={b.t} type="button" className="a-tb-btn" onClick={() => insertBlock(b.t)}>
                    {b.icon}{b.lbl}
                  </button>
                ))}
              </div>
              <div className="a-tb-r">
                <input type="file" ref={fileRef} className="a-hidden"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                <button type="button" className="a-tb-btn" disabled={uploading}
                  onClick={() => fileRef.current?.click()}>
                  <Upload size={11}/>{uploading ? "Uploading…" : "Image"}
                </button>
                <button type="button"
                  className={`a-tb-btn${preview?" a-tb-on":""}`}
                  onClick={() => setPreview(p=>!p)}>
                  <Eye size={11}/>{preview ? "Editor" : "Preview"}
                </button>
              </div>
            </div>
            {preview ? (
              <div className="a-preview"
                dangerouslySetInnerHTML={{__html: renderMarkdownPreview(form.content)}}
                data-testid="editor-preview" />
            ) : (
              <div className={`a-drop${dragging?" is-drag":""}`}
                onDragEnter={onDragEnter} onDragLeave={onDragLeave}
                onDragOver={onDragOver} onDrop={onDrop}>
                {dragging && (
                  <div className="a-drop-ov">
                    <ImageIcon size={22} color="#00ff88"/>
                    <span>Drop image here</span>
                  </div>
                )}
                <textarea ref={taRef} name="content" className="a-editor"
                  value={form.content} onChange={chg} onPaste={onPaste}
                  rows={28} spellCheck={false} placeholder={DEFAULT_TEMPLATE}
                  data-testid="editor-content" />
              </div>
            )}
            <div className="a-hint">
              Tip: paste images (Ctrl+V) or drag &amp; drop files directly into the editor
            </div>
          </div>

          {/* footer */}
          <div className="a-footer">
            <button type="button" className="a-btn a-ghost" onClick={() => navigate("/admin")}>
              <X size={13}/> Cancel
            </button>
            <button type="submit" className="a-btn a-primary" disabled={saving}
              data-testid="editor-submit">
              <Save size={13}/>
              {saving ? "Saving…" : id ? "Update Writeup" : "Publish Writeup"}
            </button>
          </div>

        </div>{/* /a-left */}

        {/* ════ RIGHT — live summary panel ════ */}
        <aside className="a-right">
          <div className="a-panel">
            <div className="a-panel-hdr"><span className="a-ph-pre">// </span>Live Preview</div>
            <div className="a-pwned">
              <div className="a-pwned-scan" />
              <span style={{fontSize:20, flexShrink:0}}>💀</span>
              <div>
                <div className="a-pwned-title">{machine} HAS BEEN PWNED</div>
                <div className="a-pwned-sub">uid=0(root) gid=0(root)</div>
              </div>
            </div>
            <div className="a-badges">
              <span className="a-badge" style={{color:"#00ff88",borderColor:"rgba(0,255,136,.4)",background:"rgba(0,255,136,.08)"}}>
                {PLAT[form.platform]||form.platform}
              </span>
              <span className="a-badge" style={{color:diff.c,borderColor:`rgba(${diff.r},.4)`,background:`rgba(${diff.r},.08)`}}>
                {form.difficulty}
              </span>
              {form.os_type && (
                <span className="a-badge" style={{color:"#00d4ff",borderColor:"rgba(0,212,255,.4)",background:"rgba(0,212,255,.08)"}}>
                  {form.os_type}
                </span>
              )}
            </div>
            {form.title && <div className="a-prev-title">{form.title}</div>}
            {form.description && <p className="a-prev-desc">{form.description}</p>}
            {form.skills.length > 0 && (
              <div className="a-panel-block">
                <div className="a-pb-lbl">Skills ({form.skills.length})</div>
                <div className="a-chips a-chips-sm">
                  {form.skills.map(s => (
                    <span key={s} className="a-chip a-chip-sm is-on">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {form.techniques.length > 0 && (
              <div className="a-panel-block">
                <div className="a-pb-lbl">Techniques ({form.techniques.length})</div>
                <div className="a-chips a-chips-sm">
                  {form.techniques.map(t => (
                    <span key={t} className="a-chip a-chip-c a-chip-sm is-on">{t}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="a-panel-block">
              <div className="a-stats">
                <span>{form.published ? "Public" : "Draft"}</span>
                <span>{tagCount} tags</span>
              </div>
            </div>
            <div className="a-panel-block">
              <div className="a-stats">
                <span>{mins} min read</span>
                <span>{words} words</span>
              </div>
            </div>
            <div className="a-panel-block">
              <div className="a-stats">
                <span>{form.techniques.length} techniques</span>
                <span>{toolCount} tools</span>
              </div>
            </div>
          </div>
          <div className="a-pub-box">
            <button type="button" className="a-btn a-outline a-full"
              onClick={(e) => handleSubmit(e, false)} disabled={saving}>
              <Save size={13}/> Save as Draft
            </button>
            <button type="button" className="a-btn a-primary a-full"
              onClick={(e) => handleSubmit(e, true)} disabled={saving}>
              {saving ? "Publishing…" : <><ChevronRight size={13}/> Publish Writeup</>}
            </button>
          </div>
        </aside>

      </form>

      {/* publish success modal */}
      <Dialog open={pubOk} onOpenChange={setPubOk}>
        <DialogContent className="a-dialog">
          <DialogHeader>
            <DialogTitle className="a-dialog-title">
              <CheckCircle size={20} color="#00ff88"/> Writeup Published
            </DialogTitle>
          </DialogHeader>
          <p className="a-dialog-body">Your writeup is live. Share it with your network!</p>
          <div className="a-dialog-acts">
            <a href={getLinkedInUrl()} target="_blank" rel="noopener noreferrer"
              className="a-li-btn" data-testid="publish-linkedin-share">
              <Linkedin size={15}/> Share on LinkedIn
            </a>
            <a href={getWriteupUrl()} target="_blank" rel="noopener noreferrer" className="a-view-btn">
              View Published Writeup →
            </a>
            <button className="a-btn a-ghost a-full"
              onClick={() => { setPubOk(false); if (!id) navigate(`/admin/writeup/${pubId}`); }}>
              Maybe Later
            </button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

/* ══════════════════════════════════════════
   CSS
══════════════════════════════════════════ */
const CSS = `
@keyframes a-blink    { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes a-fadein   { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
@keyframes a-scanline { 0%{transform:translateX(-120%)} 100%{transform:translateX(350%)} }

.a-root {
  min-height:100vh;
  background:#070a0e;
  background-image:
    linear-gradient(rgba(0,255,136,.012) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.012) 1px,transparent 1px);
  background-size:44px 44px;
  font-family:'JetBrains Mono',ui-monospace,monospace;
  color:#b8c4d0;
}
.a-loading {
  min-height:100vh; display:flex; align-items:center; justify-content:center;
  background:#070a0e; font-family:'Share Tech Mono',monospace; font-size:14px; color:#00ff88;
}
.a-blink {
  display:inline-block; width:8px; height:14px; background:#00ff88;
  vertical-align:middle; margin-left:2px; animation:a-blink 1.1s step-end infinite;
}

/* topbar */
.a-topbar {
  position:sticky; top:0; z-index:200; display:flex; align-items:center;
  justify-content:space-between; height:50px; padding:0 2rem;
  background:rgba(7,10,14,.95); backdrop-filter:blur(14px);
  border-bottom:1px solid #1a2738;
}
.a-topbar-l {
  display:flex; align-items:center; gap:8px;
  font-family:'Share Tech Mono',monospace; font-size:13px; color:#56657a;
}
.a-sep     { color:#1a2738; margin:0 2px }
.a-tp-path { font-family:'Share Tech Mono',monospace; font-size:13px; color:#56657a }
.a-back {
  display:inline-flex; align-items:center; gap:5px; padding:0;
  font-family:'JetBrains Mono',monospace; font-size:12px; color:#56657a;
  background:none; border:none; cursor:pointer; transition:color .18s;
}
.a-back:hover { color:#00ff88 }
.a-topbar-r { display:flex; gap:7px }

/* buttons */
.a-btn {
  display:inline-flex; align-items:center; gap:6px; padding:6px 14px;
  font-family:'JetBrains Mono',monospace; font-size:12px; letter-spacing:.04em;
  border-radius:3px; cursor:pointer; border:1px solid transparent;
  transition:all .18s; white-space:nowrap; -webkit-appearance:none; appearance:none;
}
.a-btn:disabled { opacity:.4; cursor:not-allowed }
.a-ghost  { background:transparent; color:#56657a; border-color:#1a2738 }
.a-ghost:hover:not(:disabled) { color:#b8c4d0; border-color:#56657a }
.a-outline { background:transparent; color:#00ff88; border-color:rgba(0,255,136,.35) }
.a-outline:hover:not(:disabled) { background:rgba(0,255,136,.07); border-color:rgba(0,255,136,.6) }
.a-primary {
  background:rgba(0,255,136,.1); color:#00ff88;
  border-color:rgba(0,255,136,.42); box-shadow:0 0 14px rgba(0,255,136,.07);
}
.a-primary:hover:not(:disabled) { background:rgba(0,255,136,.18); box-shadow:0 0 22px rgba(0,255,136,.15) }
.a-full { width:100%; justify-content:center }

/* layout */
.a-layout {
  display:grid; grid-template-columns:1fr 268px;
  gap:1.6rem; align-items:start;
  max-width:1180px; margin:0 auto; padding:2rem 2rem 6rem;
}
@media(max-width:960px) { .a-layout{grid-template-columns:1fr} .a-right{display:none} }

/* cards */
.a-card {
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:1.6rem 1.8rem; margin-bottom:1.1rem;
  animation:a-fadein .4s ease both;
}

/* section step header */
.a-step { display:flex; align-items:center; gap:9px; margin-bottom:1.3rem }
.a-step-n     { font-family:'Share Tech Mono',monospace; font-size:11px; color:#00ff88; opacity:.5; flex-shrink:0 }
.a-step-icon  { color:#00ff88; opacity:.65; flex-shrink:0 }
.a-step-label { font-family:'Syne',system-ui,sans-serif; font-size:14px; font-weight:700; color:#e2eaf3; letter-spacing:-.01em }
.a-step-rule  { flex:1; height:1px; background:linear-gradient(90deg,#1a2738,transparent) }

/* grids */
.a-g2 { display:grid; grid-template-columns:1fr 1fr; gap:1rem }
.a-g3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:1rem }
.a-g4 { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:1rem }
.a-mb { margin-bottom:1rem }
@media(max-width:700px) { .a-g2,.a-g3,.a-g4{grid-template-columns:1fr} }

/* field label */
.a-field { display:flex; flex-direction:column; gap:6px }
.a-fl { display:flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:#56657a }
.a-fl-arr { color:#00ff88; font-size:14px; line-height:1 }
.a-fl-req { color:#ff4455; font-size:12px }

/* inputs */
.a-in {
  width:100%; padding:9px 12px;
  background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#b8c4d0;
  outline:none; transition:border-color .18s;
}
.a-in::placeholder { color:#2e4a5a }
.a-in:focus { border-color:rgba(0,255,136,.4) }
.a-ta { resize:vertical; min-height:76px; line-height:1.7 }

/* select */
.a-sel-wrap { position:relative }
.a-sel {
  width:100%; padding:9px 32px 9px 12px;
  background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#b8c4d0;
  outline:none; cursor:pointer; -webkit-appearance:none; appearance:none;
  transition:border-color .18s;
}
.a-sel:focus { border-color:rgba(0,255,136,.4) }
.a-sel-wrap::after {
  content:"▾"; position:absolute; right:12px; top:50%; transform:translateY(-50%);
  color:#56657a; pointer-events:none; font-size:12px;
}

/* segmented */
.a-seg { display:flex; gap:4px; flex-wrap:wrap }
.a-seg-btn {
  padding:7px 11px; font-family:'Share Tech Mono',monospace;
  font-size:11px; text-transform:capitalize; letter-spacing:.05em;
  color:#56657a; background:transparent; border:1px solid #1a2738;
  border-radius:2px; cursor:pointer; transition:all .18s;
}
.a-seg-btn:hover { border-color:#56657a; color:#b8c4d0 }
.a-seg-cyan.is-on {
  border-color:rgba(0,212,255,.45) !important; color:#00d4ff !important;
  background:rgba(0,212,255,.08) !important;
}

/* toggle row */
.a-toggle-row {
  display:flex; align-items:center; gap:10px; height:42px; padding:0 12px;
  background:#070a0e; border:1px solid #1a2738; border-radius:3px;
}

/* chips */
.a-chips { display:flex; flex-wrap:wrap; gap:6px }
.a-chips-sm { gap:4px }
.a-chip {
  display:inline-flex; align-items:center; gap:5px;
  padding:5px 12px; border-radius:2px;
  font-family:'Share Tech Mono',monospace; font-size:11px; letter-spacing:.04em;
  color:#56657a; background:transparent; border:1px solid #1a2738;
  cursor:pointer; transition:all .18s; white-space:nowrap;
}
.a-chip:hover { border-color:rgba(0,255,136,.3); color:#00ff88 }
.a-chip.is-on {
  border-color:rgba(0,255,136,.5) !important; color:#00ff88 !important;
  background:rgba(0,255,136,.09) !important;
}
.a-chip-c:hover { border-color:rgba(0,212,255,.3); color:#00d4ff }
.a-chip-c.is-on {
  border-color:rgba(0,212,255,.5) !important; color:#00d4ff !important;
  background:rgba(0,212,255,.09) !important;
}
.a-chip-sm { padding:3px 8px; font-size:10px }
.a-dot { width:5px; height:5px; border-radius:50%; background:currentColor; flex-shrink:0 }

/* ── TAG SELECTOR ── */
.ts-wrap { display:flex; flex-direction:column; gap:0 }
.ts-search-wrap {
  position:relative; display:flex; align-items:center;
  margin-bottom:10px;
}
.ts-search-icon {
  position:absolute; left:10px; color:#2e4a5a; pointer-events:none; flex-shrink:0;
}
.ts-search {
  width:100%; padding:7px 12px 7px 30px;
  background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-family:'Share Tech Mono',monospace; font-size:11px; color:#b8c4d0;
  outline:none; transition:border-color .18s;
}
.ts-search:focus { border-color:rgba(0,255,136,.4) }
.ts-search::placeholder { color:#2e4a5a }
.ts-sel-count {
  position:absolute; right:10px;
  font-family:'Share Tech Mono',monospace; font-size:10px;
  color:#00ff88; background:#070a0e; padding:0 4px; pointer-events:none;
}
.ts-tabs {
  display:flex; gap:0; flex-wrap:wrap;
  border-bottom:1px solid #1a2738; margin-bottom:10px;
}
.ts-tab {
  display:inline-flex; align-items:center; gap:5px;
  font-family:'Share Tech Mono',monospace; font-size:10px;
  letter-spacing:.06em; text-transform:uppercase;
  color:#56657a; padding:6px 12px; cursor:pointer;
  background:none; border:none; border-bottom:2px solid transparent;
  transition:all .18s; white-space:nowrap;
}
.ts-tab:hover { color:#b8c4d0 }
.ts-tab.active { color:#00ff88; border-bottom-color:#00ff88 }
.ts-tab-cyan.active { color:#00d4ff !important; border-bottom-color:#00d4ff !important }
.ts-tab-badge {
  font-size:9px; padding:1px 5px; border-radius:8px;
  background:rgba(0,255,136,.15); color:#00ff88;
  font-family:'Share Tech Mono',monospace;
}
.ts-tab-cyan .ts-tab-badge { background:rgba(0,212,255,.15); color:#00d4ff }
.ts-body { display:flex; flex-direction:column; gap:6px; max-height:280px; overflow-y:auto; padding-right:2px }
.ts-body::-webkit-scrollbar { width:3px }
.ts-body::-webkit-scrollbar-track { background:transparent }
.ts-body::-webkit-scrollbar-thumb { background:#1a2738; border-radius:2px }
.ts-cat-label {
  font-family:'Share Tech Mono',monospace; font-size:9px;
  text-transform:uppercase; letter-spacing:.1em; color:#2e4a5a;
  margin-bottom:4px; margin-top:4px;
}
.ts-tags { display:flex; flex-wrap:wrap; gap:5px; margin-bottom:4px }
.ts-empty {
  font-family:'Share Tech Mono',monospace; font-size:11px;
  color:#2e4a5a; padding:1rem 0; text-align:center;
}

/* cover */
.a-cover-row { display:flex; gap:10px; align-items:center }
.a-cover-row .a-in { flex:1 }
.a-cover-thumb { width:64px; height:40px; object-fit:cover; flex-shrink:0; border-radius:2px; border:1px solid #1a2738 }

/* toolbar */
.a-toolbar {
  display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:6px;
  padding:8px 14px; background:#0d1520; border:1px solid #1a2738;
  border-bottom:none; border-radius:3px 3px 0 0;
}
.a-tb-l,.a-tb-r { display:flex; gap:4px; flex-wrap:wrap }
.a-tb-btn {
  display:inline-flex; align-items:center; gap:5px;
  padding:4px 10px; border-radius:2px;
  font-family:'Share Tech Mono',monospace; font-size:11px;
  color:#56657a; background:transparent; border:1px solid #1a2738;
  cursor:pointer; transition:all .18s;
}
.a-tb-btn:hover { color:#00ff88; border-color:rgba(0,255,136,.3) }
.a-tb-btn:disabled { opacity:.4; cursor:not-allowed }
.a-tb-on { color:#00ff88 !important; border-color:rgba(0,255,136,.4) !important; background:rgba(0,255,136,.07) !important }
.a-hidden { display:none }

/* dropzone */
.a-drop { position:relative }
.a-drop.is-drag { outline:2px dashed rgba(0,255,136,.55); outline-offset:2px; border-radius:0 0 3px 3px }
.a-drop-ov {
  position:absolute; inset:0; z-index:10;
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px;
  background:rgba(0,255,136,.05); border:2px dashed rgba(0,255,136,.5);
  font-family:'Share Tech Mono',monospace; font-size:13px; color:#00ff88;
  border-radius:0 0 3px 3px;
}

/* editor */
.a-editor {
  display:block; width:100%; min-height:420px; padding:1.2rem 1.4rem;
  background:#060a0d; border:1px solid #1a2738; border-top:none;
  border-radius:0 0 3px 3px;
  font-family:'Share Tech Mono',monospace; font-size:13px;
  color:#b8c4d0; line-height:1.78; outline:none; resize:vertical;
  transition:border-color .18s;
}
.a-editor:focus { border-color:rgba(0,255,136,.35) }
.a-editor::placeholder { color:#2e4a5a }

/* preview */
.a-preview {
  min-height:420px; max-height:640px; overflow-y:auto;
  padding:1.4rem 1.6rem;
  background:#060a0d; border:1px solid #1a2738; border-top:none;
  border-radius:0 0 3px 3px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#b8c4d0; line-height:1.8;
}
.apv-h1 { font-family:'Syne',sans-serif; font-size:1.5rem; font-weight:800; color:#e2eaf3; margin:1.5rem 0 .6rem }
.apv-h2 { font-family:'Syne',sans-serif; font-size:1.2rem; font-weight:700; color:#e2eaf3; margin:1.2rem 0 .5rem; border-bottom:1px solid #1a2738; padding-bottom:.35rem }
.apv-h3 { font-family:'Syne',sans-serif; font-size:1rem; font-weight:700; color:#b8c4d0; margin:1rem 0 .35rem }
.apv-terminal { background:#060a0d; border:1px solid rgba(0,255,136,.2); border-left:3px solid #00ff88; padding:1rem; border-radius:0 3px 3px 0; font-family:'Share Tech Mono',monospace; font-size:12px; color:#00ff88; overflow-x:auto }
.apv-output   { background:#060a0d; border:1px solid rgba(0,212,255,.2); border-left:3px solid #00d4ff; padding:1rem; border-radius:0 3px 3px 0; font-family:'Share Tech Mono',monospace; font-size:12px; color:#00d4ff; overflow-x:auto }
.apv-code     { background:#060a0d; border:1px solid #1a2738; padding:1rem; border-radius:3px; font-family:'Share Tech Mono',monospace; font-size:12px; color:#b8c4d0; overflow-x:auto }
.apv-ic       { background:rgba(0,255,136,.08); border:1px solid rgba(0,255,136,.16); color:#00ff88; padding:1px 6px; border-radius:3px; font-size:12px }
.apv-bq       { border-left:3px solid #00ff88; background:rgba(0,255,136,.03); padding:.6rem 1rem; color:#00cc6a; font-style:italic }
.apv-a        { color:#00d4ff; border-bottom:1px dotted rgba(0,212,255,.4); text-decoration:none }
.apv-img      { max-width:100%; border-radius:3px; border:1px solid #1a2738; margin:.8rem 0; display:block }

.a-hint { font-size:10px; color:#2e4a5a; letter-spacing:.03em; margin-top:8px }

/* footer */
.a-footer { display:flex; justify-content:flex-end; gap:9px; padding-top:1.2rem; border-top:1px solid #1a2738 }

/* right column */
.a-right { position:sticky; top:62px; display:flex; flex-direction:column; gap:1rem }
.a-panel { background:#0c1118; border:1px solid #1a2738; border-radius:3px; padding:1.4rem }
.a-panel-hdr { font-size:10px; text-transform:uppercase; letter-spacing:.12em; color:#56657a; margin-bottom:1rem }
.a-ph-pre { color:#00ff88 }
.a-pwned {
  position:relative; overflow:hidden; display:flex; align-items:center; gap:10px;
  padding:.85rem 1rem; margin-bottom:1rem;
  background:linear-gradient(135deg,#0c1118,#091209);
  border:1px solid rgba(0,255,136,.15); border-radius:3px;
  font-family:'Share Tech Mono',monospace;
}
.a-pwned-scan {
  position:absolute; top:0; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,#00ff88,transparent);
  animation:a-scanline 4s linear infinite;
}
.a-pwned-title { font-size:11px; color:#00ff88; letter-spacing:.07em; line-height:1.3 }
.a-pwned-sub   { font-size:10px; color:#56657a; margin-top:3px }
.a-badges { display:flex; flex-wrap:wrap; gap:5px; margin-bottom:1rem }
.a-badge  {
  font-family:'Share Tech Mono',monospace; font-size:10px;
  padding:3px 9px; border-radius:2px; border:1px solid;
  letter-spacing:.06em; text-transform:uppercase;
}
.a-prev-title {
  font-family:'Syne',system-ui,sans-serif; font-size:15px; font-weight:700;
  color:#e2eaf3; letter-spacing:-.01em; margin-bottom:.5rem; line-height:1.2;
}
.a-prev-desc { font-size:11.5px; color:#56657a; line-height:1.65; margin:0 0 .8rem }
.a-panel-block { margin-top:.9rem; padding-top:.9rem; border-top:1px solid #1a2738 }
.a-pb-lbl { font-size:10px; text-transform:uppercase; letter-spacing:.1em; color:#2e4a5a; margin-bottom:.5rem }
.a-stats  { display:flex; justify-content:space-between; font-family:'Share Tech Mono',monospace; font-size:11px; color:#56657a }
.a-pub-box {
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:1rem; display:flex; flex-direction:column; gap:8px;
}

/* dialog */
.a-dialog {
  background:#0c1118 !important; border:1px solid #1a2738 !important;
  border-radius:3px !important; font-family:'JetBrains Mono',monospace; max-width:420px;
}
.a-dialog-title {
  display:flex; align-items:center; gap:8px;
  font-family:'Syne',system-ui,sans-serif; font-size:17px; font-weight:700; color:#e2eaf3;
}
.a-dialog-body { font-size:13px; color:#56657a; margin:1rem 0 1.4rem; line-height:1.7 }
.a-dialog-acts { display:flex; flex-direction:column; gap:8px }
.a-li-btn {
  display:flex; align-items:center; justify-content:center; gap:8px;
  padding:11px; border-radius:3px; background:#0A66C2; color:#fff;
  font-family:'JetBrains Mono',monospace; font-size:13px; text-decoration:none; transition:background .18s;
}
.a-li-btn:hover { background:#004182 }
.a-view-btn {
  text-align:center; font-size:12px; color:#00ff88; text-decoration:none;
  font-family:'Share Tech Mono',monospace; letter-spacing:.03em;
}
.a-view-btn:hover { text-decoration:underline }
`;

export default AdminWriteupEditorTest;
